// src-lit/components/product/kd-pantry-products.ts
import { LitElement, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';
import {
  EVENT_NAMES,
  type PantryProductQuantityChangedDetail,
  type PantryStateChangedDetail,
} from '../../constants/events';
import { SELECTORS } from '../../constants/selectors';
import type { KdPantryProductQuantity } from './kd-pantry-product-quantity';
import {
  type PantryCardMeta,
  type PantrySortKey,
  type SelectedPantryEntry,
  calcPantryTotals,
  computePantryCardOrder,
  isPantryCardVisible,
} from './pantry-products.logic';

/**
 * @element kd-pantry-products
 * @description えらんでKuradashi 商品選択モジュールのルートコンテナ。
 *   配下の <kd-pantry-product-quantity> が dispatch する数量変更イベントを集約し、
 *   合計金額・個数・重量・在庫超過の状態を計算して `kd-pantry-state-changed` を
 *   document に発火する。FV 側（issue #3105 で実装済み）の <kd-product-cart
 *   data-is-pantry="true"> がそのイベントを購読して右カラム表示を更新する。
 *
 *   カート投入フロー（properties に _pantryKey を載せて submit）は <kd-pantry-cart-add>
 *   (issue #3090) が担当。本コンポーネントは集計と通知に加え、`getSelectedEntries()`
 *   で選択中エントリを公開して連携する。
 */
@customElement('kd-pantry-products')
export class KdPantryProducts extends LitElement {
  createRenderRoot() {
    return this;
  }

  private entries = new Map<string, SelectedPantryEntry>();

  /** 現在のカテゴリ絞り込み（'' = 全て）。 */
  private activeCategory = '';

  /** 現在の並び替えキー。 */
  private sortKey: PantrySortKey = 'popular';

  private tabButtons: HTMLButtonElement[] = [];

  private sortSelect: HTMLSelectElement | null = null;

  private countEl: HTMLElement | null = null;

  private cardEls: HTMLElement[] = [];

  private cardMetas: PantryCardMeta[] = [];

  /**
   * 選択中のパントリー子エントリのスナップショットを返す。
   * <kd-pantry-cart-add> がカート投入時に呼び出す。
   */
  getSelectedEntries(): SelectedPantryEntry[] {
    return Array.from(this.entries.values());
  }

  private boundOnQuantityChanged = this.onQuantityChanged.bind(this);

  private boundOnTabClick = this.onTabClick.bind(this);

  private boundOnSortChange = this.onSortChange.bind(this);

  private boundOnClearRequested = this.onClearRequested.bind(this);

  render() {
    return nothing;
  }

  connectedCallback(): void {
    super.connectedCallback();
    this.addEventListener(
      EVENT_NAMES.PANTRY.QUANTITY_CHANGED,
      this.boundOnQuantityChanged as EventListener,
    );

    // ツールバー（カテゴリタブ / 並び替え / 件数）とフラットグリッドのカードを収集。
    // カードは SSR 済みのため、絞り込み（display）と並び替え（CSS order）を
    // DOM 非移動で行う（カード内の選択状態・数量 peer 同期を壊さない）。
    this.tabButtons = Array.from(
      this.querySelectorAll<HTMLButtonElement>(SELECTORS.PANTRY_PRODUCTS.TAB),
    );
    this.sortSelect = this.querySelector<HTMLSelectElement>(
      SELECTORS.PANTRY_PRODUCTS.SORT_SELECT,
    );
    this.countEl = this.querySelector<HTMLElement>(SELECTORS.PANTRY_PRODUCTS.COUNT);
    this.cardEls = Array.from(
      this.querySelectorAll<HTMLElement>(SELECTORS.PANTRY_PRODUCTS.CARD),
    );
    this.cardMetas = this.cardEls.map((el, index) => ({
      index,
      category: el.dataset.pantryCategory ?? '',
      price: Number(el.dataset.sortPrice) || 0,
      expiration: el.dataset.sortExpiration ?? '',
    }));
    // タブはイベント委譲（ボタンが複数のため root で 1 本受ける）。
    this.addEventListener('click', this.boundOnTabClick);
    this.sortSelect?.addEventListener('change', this.boundOnSortChange);
    // フッターの「クリア」は本要素の外側（kd-product-cart）にあるため document 経由で受ける。
    document.addEventListener(
      EVENT_NAMES.PANTRY.CLEAR_REQUESTED,
      this.boundOnClearRequested as EventListener,
    );
    this.applyFilterSort();

    // FV 側が初期描画時から合計 0 円・最低金額未到達の状態を表示できるよう
    // 接続直後に一度だけ初期状態を通知する。
    this.dispatchState();
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.removeEventListener(
      EVENT_NAMES.PANTRY.QUANTITY_CHANGED,
      this.boundOnQuantityChanged as EventListener,
    );
    this.removeEventListener('click', this.boundOnTabClick);
    this.sortSelect?.removeEventListener('change', this.boundOnSortChange);
    document.removeEventListener(
      EVENT_NAMES.PANTRY.CLEAR_REQUESTED,
      this.boundOnClearRequested as EventListener,
    );
    this.tabButtons = [];
    this.sortSelect = null;
    this.countEl = null;
    this.cardEls = [];
    this.cardMetas = [];
    this.entries.clear();
  }

  private onQuantityChanged(event: CustomEvent<PantryProductQuantityChangedDetail>): void {
    const d = event.detail;
    if (!d.variantId) return;

    if (d.quantity <= 0) {
      this.entries.delete(d.variantId);
    } else {
      this.entries.set(d.variantId, {
        variantId: d.variantId,
        quantity: d.quantity,
        variantPrice: d.variantPrice,
        weightG: d.weightG,
        inventoryQuantity: d.inventoryQuantity,
        maxQuantity: d.maxQuantity,
        title: d.title,
        sku: d.sku,
        expirationOn: d.expirationOn,
        volume: d.volume,
      });
    }
    this.dispatchState();
  }

  /**
   * 「クリア」要求。配下の全 <kd-pantry-product-quantity> を 0 に戻す。
   * 各要素の reset() が QUANTITY_CHANGED を発火し、onQuantityChanged 経由で
   * entries から削除 → dispatchState まで既存経路で伝播する。
   */
  private onClearRequested(): void {
    const quantities = Array.from(
      this.querySelectorAll<KdPantryProductQuantity>(
        SELECTORS.PANTRY_PRODUCTS.QUANTITY_ELEMENT,
      ),
    );
    quantities.forEach((el) => el.reset());
  }

  /** カテゴリタブ押下（イベント委譲）。activeCategory を切替えて再描画する。 */
  private onTabClick(event: Event): void {
    const target = event.target as HTMLElement | null;
    const tab = target?.closest<HTMLButtonElement>(SELECTORS.PANTRY_PRODUCTS.TAB);
    if (!tab || !this.contains(tab)) return;
    const category = tab.dataset.category ?? '';
    if (category === this.activeCategory) return;
    this.activeCategory = category;
    this.applyFilterSort();
  }

  /** 並び替え <select> 変更。sortKey を切替えて再描画する。 */
  private onSortChange(): void {
    const value = this.sortSelect?.value;
    if (value !== 'popular' && value !== 'expiration' && value !== 'price') return;
    if (value === this.sortKey) return;
    this.sortKey = value;
    this.applyFilterSort();
  }

  /**
   * カテゴリ絞り込み（display）・並び替え（CSS order）・タブ active 状態・件数を反映する。
   * DOM は移動しないため、カード内の <kd-pantry-product-quantity> の選択状態は保持される。
   */
  private applyFilterSort(): void {
    const order = computePantryCardOrder(this.cardMetas, this.sortKey);
    let visibleCount = 0;
    this.cardEls.forEach((el, index) => {
      const meta = this.cardMetas[index];
      const visible = isPantryCardVisible(meta.category, this.activeCategory);
      el.style.display = visible ? '' : 'none';
      el.style.order = String(order.get(index) ?? index);
      if (visible) visibleCount += 1;
    });
    this.tabButtons.forEach((tab) => {
      const isActive = (tab.dataset.category ?? '') === this.activeCategory;
      tab.dataset.active = isActive ? 'true' : 'false';
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });
    if (this.countEl) this.countEl.textContent = String(visibleCount);
  }

  /** data 属性を数値で読む（非数値・未設定は 0）。minimumPrice / 親 base 送料の取得に共用。 */
  private getDatasetNumber(key: string): number {
    const v = Number(this.dataset[key] ?? 0);
    return Number.isFinite(v) ? v : 0;
  }

  private dispatchState(): void {
    // 親 base 送料（円）を送料込み合計に加算する（SSoT: shipping.md §6.1。子は送料対象外 / cool は親も 0）。
    // 最大重量（g）は data-max-box-weight-g（metafield 由来、fallback 10000）から読み、超過判定に使う（issue #3179）。
    const totals = calcPantryTotals(
      Array.from(this.entries.values()),
      this.getDatasetNumber('shippingBaseGeneral'),
      this.getDatasetNumber('shippingBasePremium'),
      this.getDatasetNumber('maxBoxWeightG'),
    );
    const detail: PantryStateChangedDetail = {
      ...totals,
      minimumPrice: this.getDatasetNumber('minimumPrice'),
    };
    document.dispatchEvent(
      new CustomEvent<PantryStateChangedDetail>(EVENT_NAMES.PANTRY.STATE_CHANGED, {
        bubbles: true,
        composed: true,
        detail,
      }),
    );
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'kd-pantry-products': KdPantryProducts;
  }
}
